import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

dayjs.extend(relativeTime);

const RecentlyAddedShiftLogs = ({ location, endpoint }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shiftLogs, setShiftLogs] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen]);

  const loadShiftLogs = async (cursor = null) => {
    setIsLoading(true);
    setError("");

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set("location", location);
      if (cursor) url.searchParams.set("cursor", cursor);

      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) throw new Error("Unable to load recently added shift logs.");

      const data = await response.json();
      setShiftLogs((currentLogs) =>
        cursor ? [...currentLogs, ...data.shift_logs] : data.shift_logs
      );
      setNextCursor(data.next_cursor);
      setHasMore(data.has_more);
    } catch (_error) {
      setError("We couldn't load recently added shift logs. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setShiftLogs([]);
    setNextCursor(null);
    setHasMore(false);
    setIsOpen(true);
    loadShiftLogs();
  };

  const retryLoad = () => loadShiftLogs(shiftLogs.length > 0 ? nextCursor : null);

  return (
    <div className="recently-added-shift-logs">
      <button
        type="button"
        className="button recently-added-shift-logs__trigger"
        onClick={openModal}
        {...{ explorator_test_id: "recently-added-shift-logs-button" }}
      >
        <span className="icon" aria-hidden="true">
          <i className="far fa-clock"></i>
        </span>
        <span>Recently Added</span>
      </button>

      {isOpen && (
        <div className="modal is-active recently-added-shift-logs__modal">
          <div className="modal-background" onClick={() => setIsOpen(false)}></div>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recently-added-title"
          >
            <header className="modal-card-head">
              <div>
                <p id="recently-added-title" className="modal-card-title">
                  Recently Added Shift Logs
                  <span className="recently-added-shift-logs__location"> — {location}</span>
                </p>
              </div>
              <button
                type="button"
                className="delete"
                aria-label="Close recently added shift logs"
                onClick={() => setIsOpen(false)}
              ></button>
            </header>

            <section className="modal-card-body">
              <p className="recently-added-shift-logs__explanation">
                Added in the past 24 hours, across all dates and shifts.
              </p>

              {error && (
                <div
                  className="notification is-danger recently-added-shift-logs__error"
                  role="alert"
                >
                  <span>{error}</span>
                  <button type="button" className="button is-light" onClick={retryLoad}>
                    Try again
                  </button>
                </div>
              )}

              {isLoading && shiftLogs.length === 0 && (
                <div className="recently-added-shift-logs__status" role="status">
                  <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
                  <span>Loading recently added shift logs…</span>
                </div>
              )}

              {!isLoading && !error && shiftLogs.length === 0 && (
                <div className="recently-added-shift-logs__empty">
                  <i className="far fa-clock" aria-hidden="true"></i>
                  <p>No shift logs were added at this location in the past 24 hours.</p>
                </div>
              )}

              {shiftLogs.length > 0 && (
                <div className="recently-added-shift-logs__list">
                  {shiftLogs.map((shiftLog) => (
                    <a
                      key={shiftLog.id}
                      href={shiftLog.view_url}
                      className="recently-added-shift-logs__item"
                      {...{
                        explorator_test_id: `recently-added-shift-log-${shiftLog.id}`
                      }}
                    >
                      <div className="recently-added-shift-logs__item-heading">
                        <strong>{shiftLog.client_name}</strong>
                        <span>Added {dayjs(shiftLog.created_at).fromNow()}</span>
                      </div>
                      <div className="recently-added-shift-logs__metadata">
                        <span>
                          <i className="far fa-user" aria-hidden="true"></i>
                          {shiftLog.logged_by || "Unknown staff member"}
                        </span>
                        <span>
                          <i className="far fa-calendar" aria-hidden="true"></i>
                          Saved for {dayjs(`${shiftLog.log_date}T12:00:00`).format(
                            "MMM D, YYYY"
                          )} · {shiftLog.shift_num} shift
                        </span>
                      </div>
                      <p className="recently-added-shift-logs__preview">
                        {shiftLog.log_description || "No description provided."}
                      </p>
                      <span className="recently-added-shift-logs__open-label">
                        View shift log <i className="fas fa-chevron-right" aria-hidden="true"></i>
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </section>

            <footer className="modal-card-foot">
              {hasMore && (
                <button
                  type="button"
                  className={`button is-primary ${isLoading ? "is-loading" : ""}`}
                  disabled={isLoading}
                  onClick={() => loadShiftLogs(nextCursor)}
                  {...{ explorator_test_id: "load-more-recently-added-shift-logs" }}
                >
                  Load 10 more
                </button>
              )}
              <button type="button" className="button" onClick={() => setIsOpen(false)}>
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

RecentlyAddedShiftLogs.propTypes = {
  location: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired
};

export default RecentlyAddedShiftLogs;
